import { actionTypeKeys } from "./actionTypeKeys";
import { Branch, BranchHours } from "../../common/types/branch";
import { AnyAction, Dispatch } from "redux";
import BranchesApi from "../../common/api/branchesApi";
import { GoogleMapsApi } from "../../common/api/googleMapsApi";
import { dispatchActionError } from "src/common/actions/actionErrorHandler";
import { DateTimeUtilities } from "../utilities/dateTimeUtilities";

export const loadBranchesSuccess = (branches: Branch[]): AnyAction => {
  return {
    type: actionTypeKeys.LOAD_BRANCHES_SUCCESS,
    branches
  };
};

export const loadAllBranchHoursSuccess = (hours: BranchHours[]): AnyAction => {
  return {
    type: actionTypeKeys.LOAD_ALL_BRANCH_HOURS_SUCCESS,
    hours
  };
};

export const selectBranch = (branch: Branch): AnyAction => {
  return {
    type: actionTypeKeys.BRANCH_SELECTED,
    branch
  };
};

export const loadAllBranchHours = async (dispatch: Dispatch) => {
  try {
    const hours = await BranchesApi.getAllBranchHours();
    hours.sort(sortBranchHoursByWeekday);
    dispatch(loadAllBranchHoursSuccess(hours));
  } catch (error) {
    dispatchActionError(error, dispatch);
  }
};

export const loadBranches = (reasonId?: number, loadDistances: boolean = true) => {
  return async (dispatch: Dispatch) => {
    try {
      let branches = await BranchesApi.getBranches(reasonId);
      try {
        if (branches.length > 0) {
          if (loadDistances) {
            branches = await loadBranchDistances(branches);
          } else {
            branches = branches.sort(branchAlphabeticalCompareFunction);
          }
        }
      } finally {
        dispatch(loadBranchesSuccess([...branches]));
      }
    } catch (error) {
      dispatchActionError(error, dispatch);
    }
  };
};

export const loadBranchDistances = async (branches: Branch[]): Promise<Branch[]> => {
  const promise = new Promise<Branch[]>((resolve, reject) => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        position => {
          GoogleMapsApi.GetDistances(position, [...branches])
            .then((res: google.maps.DistanceMatrixResponse) => {
              if (res.rows.length > 0 && res.rows[0].elements.length === branches.length) {
                const branchesCopy: Branch[] = branches
                  .map((b, i) => {
                    if (b.isVirtual) {
                      return { ...b, distance: { text: "0 mi", value: 0 } };
                    } else {
                      return { ...b, distance: res.rows[0].elements[i].distance };
                    }
                  })
                  .sort(branchDistanceCompareFunction);
                resolve(branchesCopy);
              }
              resolve(branches);
            })
            .catch(error => {
              reject(error);
            });
        },
        () => {
          resolve(branches.sort(branchAlphabeticalCompareFunction));
        }
      );
    } else {
      resolve(branches.sort(branchAlphabeticalCompareFunction));
    }
  });
  return promise;
};

const branchDistanceCompareFunction = (a: Branch, b: Branch) => {
  if (a.distance && b.distance) {
    if (b.distance.value > a.distance.value) {
      return -1;
    } else if (b.distance.value < a.distance.value) {
      return 1;
    } else {
      return 0;
    }
  } else {
    return 0;
  }
};

const branchAlphabeticalCompareFunction = (a: Branch, b: Branch) => {
  if (a.name > b.name) {
    return 1;
  }
  if (a.name < b.name) {
    return -1;
  }
  return 0;
};

const sortBranchHoursByWeekday = (a: BranchHours, b: BranchHours) => {
  const aVal: number = DateTimeUtilities.getWeekdayCodeNumber(a.weekDayCode);
  const bVal: number = DateTimeUtilities.getWeekdayCodeNumber(b.weekDayCode);

  if (aVal > bVal) {
    return 1;
  } else if (aVal < bVal) {
    return -1;
  } else {
    return 0;
  }
};
